import "./account-confirm-delete.css";

import { EventTypes } from "../../../core/event-bus";
import { Data } from "../../../models/data";
import { ProfileService } from "../../../services/profile-service";
import { DataLayerService } from '../../../services/data-layer-service';
import { LoaderSpinner } from "../../shared/LoaderSpinner/loader-spinner";
import { Modal } from "../modal";
import { Modals } from "../../../types/enums";
import { EventEmitter } from "eventemitter3";
import { TimedMessage } from "../../shared/TimedMessage/timed-message";
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";
import { InputValidator } from "../../../validators/input-validator";
import { FormController } from "../../shared/Validation/form-controller";
import { Util } from "../../../utils/util";
import { ValidationSubmitGuard } from "../../shared/Validation/validation-submit-guard";

export class AccountConfirmDelete extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;
    private _form!: FormController;

    private readonly _password: HTMLInputElement | null;
    private readonly _submit: HTMLElement | null;
    private readonly _cancel: HTMLElement | null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService) {
        super("modal_account_confirm_delete", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Confirm Account Deletion" }, Modals.ACCOUNT_CONFIRM_DELETE);
        this._eventBus = eventBus;
        this._data = data;
        this._profileService = profileService;

        this._password = document.getElementById("txt_account_confirm_delete_password") as HTMLInputElement;
        this._submit = document.getElementById("btn_account_confirm_delete_submit");
        this._cancel = document.getElementById("btn_account_confirm_delete_cancel");
    }

    public override init(): void {
        super.init();
        this._setEventListeners();
        this._setEventBusListener();
        this._setTwoWayListeners();
    }

    public override open(): void {
        super.open();
        this._setForm();
        if (this._password)
            this._password.value = "";
        Util.ensureAnnouncerPresent();
    }

    public override close(): void {
        this._form?.destroy();
        super.close();
    }

    private _setForm(): void {
        this._form?.destroy();
        this._form = new FormController(this._localizationService, this._dataLayer);

        this._form.register("password", {
            inputId: "txt_account_confirm_delete_password",
            errorId: "spn_account_confirm_delete_password_error",
            rules: [
                {
                    message: "InvalidPasswordLogin",
                    validate: async (v) => await this._profileService.checkPassword(v),
                    async: true
                }
            ]
        });
    }

    protected readonly _setEventListeners = (): void => {
        const passwordToggleShow: HTMLElement | null = document.getElementById("txt_account_confirm_delete_show");
        const passwordToggleHide: HTMLElement | null = document.getElementById("txt_account_confirm_delete_hide");

        passwordToggleShow?.addEventListener("click", () => {
            this._password?.setAttribute("type", "text")
            passwordToggleShow && (passwordToggleShow.style.display = "none");
            passwordToggleHide && (passwordToggleHide.style.display = "");
        })

        passwordToggleHide?.addEventListener("click", () => {
            this._password?.setAttribute("type", "password")
            passwordToggleShow && (passwordToggleShow.style.display = "");
            passwordToggleHide && (passwordToggleHide.style.display = "none");
        })

        passwordToggleHide?.click();

        this._submit?.addEventListener("click", this._submitPassword)
        this._cancel?.addEventListener("click", () => this.close())

    }

    protected readonly _setEventBusListener = (): void => {
    }

    private readonly _setTwoWayListeners = (): void => {

    }

    private readonly _guardSubmitValidation = async (): Promise<boolean> => {
        return await ValidationSubmitGuard.validateAndAnnounce({
            container: "modal_account_confirm_delete",
            validate: async () => await this._form.validate()
        });
    }

    private readonly _submitPassword = async () => {
        if (!(await this._guardSubmitValidation())) {
            return;
        }

        const password = this._password?.value;
        const profileId = this._profileService.user.getProperty("ProfileId");

        if (!this._password?.value || !profileId) return;

        const spinner = new LoaderSpinner(this._element);
        const success = await this._profileService.deleteProfile(profileId, password || "")
        spinner.destroy();

        if (success) {
            this._profileService.signOut();
            this._eventBus.emit(EventTypes.LOGGED_OUT);
            this.close()
            const message = new TimedMessage(this._localizationService.names("AccountDeleteSuccess"), "", 1500);
            message.show();

            //send event to data layer
            if (this._dataLayer)
                this._dataLayer.delete_profile(this._profileService.user.user);
        }
        else {
            const message = new TimedMessage(this._localizationService.names("AccountDeleteFailure"), "", 1500);
            message.show();
            InputValidator.trackValidationErrorByLtag("AccountDeleteFailure", this._localizationService, this._dataLayer)
        }
    }
}

